import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  CheckCircle,
  Star,
  Speed,
  Palette,
  Phone,
  Notifications,
  Dashboard,
} from '@mui/icons-material';
import EnhancedHeader from './EnhancedHeader';

const HeaderDemo = () => {
  const features = [
    {
      icon: <Dashboard />,
      title: 'Modern Navigation',
      description: 'Clean, intuitive navigation with active state indicators and hover effects',
    },
    {
      icon: <Notifications />,
      title: 'Smart Notifications',
      description: 'Real-time notification center with unread badges and detailed previews',
    },
    {
      icon: <Phone />,
      title: 'Mobile Responsive',
      description: 'Fully responsive design with collapsible mobile drawer navigation',
    },
    {
      icon: <Palette />,
      title: 'Modern Design',
      description: 'Contemporary UI with smooth animations and professional styling',
    },
    {
      icon: <Speed />,
      title: 'Performance Optimized',
      description: 'Lightweight components with efficient rendering and smooth interactions',
    },
    {
      icon: <CheckCircle />,
      title: 'Accessibility First',
      description: 'Built with accessibility in mind, keyboard navigation, and screen reader support',
    },
  ];

  const improvements = [
    'Modern Material-UI components with consistent theming',
    'Enhanced user profile dropdown with quick actions',
    'Real-time notification system with badge indicators',
    'Responsive mobile-first design with drawer navigation',
    'Smooth animations and micro-interactions',
    'Active navigation state indicators',
    'Professional gradient backgrounds and shadows',
    'Accessibility improvements and keyboard support',
    'Clean typography and proper spacing',
    'Integration with routing and navigation',
  ];

  return (
    <>
      <EnhancedHeader />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            Enhanced Header Demo
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Experience the modern, responsive header design with improved UX
          </Typography>
          
          <Alert severity="info" sx={{ mb: 4 }}>
            <AlertTitle>Enhanced Header Features</AlertTitle>
            Try clicking on the user avatar, notification icon, and navigation items to see the enhanced functionality in action.
          </Alert>
        </Box>

        <Grid container spacing={4}>
          {/* Features Section */}
          <Grid item xs={12}>
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Key Features
                </Typography>
                <Grid container spacing={3}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          bgcolor: 'primary.light', 
                          borderRadius: 2,
                          color: 'primary.contrastText',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {feature.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Improvements List */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  UI/UX Improvements
                </Typography>
                <List>
                  {improvements.map((improvement, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={improvement}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Technical Details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Technical Implementation
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Technologies Used:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip label="React" color="primary" size="small" />
                    <Chip label="Material-UI" color="primary" size="small" />
                    <Chip label="React Router" color="secondary" size="small" />
                    <Chip label="CSS3 Animations" color="secondary" size="small" />
                    <Chip label="Responsive Design" color="info" size="small" />
                  </Box>
                </Box>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key Components:
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Star fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="AppBar with sticky positioning"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Star fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Mobile drawer navigation"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Star fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="User menu with dropdowns"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Star fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Notification center with badges"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Instructions */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  How to Use the Enhanced Header
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        Desktop Navigation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click on any navigation item to see the active state. The current page is highlighted with a different color and weight.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        User Menu
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click on your avatar to access account settings, preferences, and logout options.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        Notifications
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The notification bell shows unread count and provides a dropdown with recent notifications.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default HeaderDemo;
